import React from "react";
import { Channel } from "core/models";

type ContextType = {
    channels: Channel[];
    urls: string[];
    addURL(url: string): void;
    removeURL(url: string): void;
    addChannel(channel: Channel): void;
    removeChannel(channel: Channel): void;
    removeAll(): void;
};

const LivestreamContext = React.createContext<null | ContextType>(null);
const useLivestreamContext = (): ContextType => {
    const context = React.useContext(LivestreamContext);
    if (context === null) {
        throw new Error("Unable to create context");
    }
    return context;
};

type Props = {
    children: React.ReactNode;
};

const LivestreamContextProvider: React.FC<Props> = ({ children }) => {
    const [urls, setURLs] = React.useState<string[]>([]);
    const [channels, setChannels] = React.useState<Channel[]>([]);

    const addURL = (url: string) => {
        const newList = [...urls];
        newList.push(url);
        setURLs(newList);
    };
    const removeURL = (url: string) => {
        const newList = [...urls];
        const itemIdx = newList.findIndex((x) => x === url);
        newList.splice(itemIdx, 1);
        setURLs(newList);
    };

    const addChannel = (channel: Channel) => {
        // Disallow adding the same channel twice
        if (channels.find((x) => x.exStream.name === channel.exStream.name)) {
            return;
        }
        const newList = [...channels];
        newList.push(channel);
        setChannels(newList);
    };

    const removeChannel = (channel: Channel) => {
        const newList = [...channels];
        const itemIdx = newList.findIndex(
            (x) => x.exStream.name === channel.exStream.name,
        );
        newList.splice(itemIdx, 1);
        setChannels(newList);
    };

    const removeAll = (): void => {
        setChannels([]);
    };

    const context: ContextType = {
        channels,
        urls,
        addURL,
        removeURL,
        addChannel,
        removeChannel,
        removeAll,
    };

    return (
        <LivestreamContext.Provider value={context}>
            {children}
        </LivestreamContext.Provider>
    );
};

export { useLivestreamContext, LivestreamContextProvider };
export default LivestreamContext;
